// Buildings family, writes and meter points (TZ-2 port wave, SEN-395): building CRUD, legal companies and country
// responsibles, meter points and their data sources, history loading, asset confirmation.
// Reads ./buildings.ts for the Building shape. NomOS is the only engine: this module reshapes records and writes
// them through the generic record API (a person's edit applies at once); it never recomputes an aggregate.
// Ids: a record that came from Sentinel answers with its sentinel_*_id, one made on NomOS with its record id.
import type {
  Building, BuildingCreate, BuildingUpdate, BulkMeterPointListResponse, DataSourceSummary,
  MeterFreshnessStatus, MeterPoint, MeterPointListResponse, MeterPointUpdate,
} from '@/types';
import type {
  AssignBody, AssignResult, ConfirmResult, CountryResponsibleResult, ProposeResult, ResponsibleBody, ResponsibleCandidate,
} from '@/types/quality';
import {
  attributeIds, getConfig, getMe, nomosFetch, queryRecords, refs, scalar, toAxiosError, type NomosEnvelope, type NomosFilter,
} from '../client';
import { resolveRecordId, type IdKind } from '../ids';
import type { AnyFn, NomosRegistry } from '../registry';
import { buildingByRecordId } from './buildings';
import { NomosGapError } from './fieldops';
import * as R from './legalEntityRules';
import { countryCodes, countryRecordId } from './lookups';

// ---- small shared helpers ------------------------------------------------------------------------------------------

type Cells = Record<string, unknown>;
interface Row { id: string; v: Cells }

const str = (x: unknown): string | null => { const s = scalar(x); return typeof s === 'string' && s !== '' ? s : null; };
const num = (x: unknown): number | null => { const s = scalar(x); return typeof s === 'number' ? s : typeof s === 'string' && s !== '' && !isNaN(Number(s)) ? Number(s) : null; };
const bool = (x: unknown): boolean | null => { const s = scalar(x); return typeof s === 'boolean' ? s : null; };
const ref = (x: unknown): string | null => refs(x)[0]?.id ?? null;
const day = (x: unknown): string | null => { const s = str(x); return s ? s.slice(0, 10) : null; };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const err = (status: number, detail: string, url: string, code?: string) =>
  toAxiosError(status, code ? { detail, error: { code, message: detail } } : { detail }, url);
const sorted = <T>(xs: T[]) => [...xs].sort();

/** Every record of `type` matching `filter`, with `fields` (pages of 1000): page 1, then the rest of its total at once. */
export async function allRows(type: string, fields: string[], filter?: NomosFilter): Promise<Row[]> {
  const page = (offset: number) => queryRecords(type, { fields, filter, limit: 1000, cursor: offset });
  const first = await page(0);
  const offsets: number[] = [];
  const size = first.rows.length; // the server's page size (1000 unless it caps lower)
  if (size) for (let o = size; o < first.total; o += size) offsets.push(o);
  const pages = [first, ...await Promise.all(offsets.map(page))];
  return pages.flatMap((res) => res.rows.map((r) => ({ id: r.record_id, v: r.values })));
}

/** Cells of known records (records/values re-keyed by slug); absent/archived ids are missing from the map. */
async function cellsOf(type: string, ids: string[], fields: string[]): Promise<Map<string, Cells>> {
  const out = new Map<string, Cells>();
  if (!ids.length) return out;
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += 1000) chunks.push(ids.slice(i, i + 1000)); // records/values takes 1000 ids
  const [pages, attr] = await Promise.all([
    Promise.all(chunks.map((chunk) => nomosFetch<NomosEnvelope<{ values?: Record<string, Record<string, unknown>> }>>(
      'POST', `/objects/${type}/records/values`, { record_ids: chunk, attributes: fields }))),
    attributeIds(type),
  ]);
  const slugOf = Object.fromEntries(Object.entries(attr).map(([slug, aid]) => [aid, slug]));
  for (const vr of pages) {
    for (const [rid, cells] of Object.entries(vr.data?.values ?? {})) {
      if (Object.keys(cells).length) out.set(rid, Object.fromEntries(Object.entries(cells).map(([aid, v]) => [slugOf[aid] ?? aid, v])));
    }
  }
  return out;
}

const createRecord = async (type: string, values: Cells): Promise<string> =>
  (await nomosFetch<NomosEnvelope<{ record_id: string }>>('POST', `/objects/${type}/records`, { values })).data.record_id;
const patch = (type: string, id: string, values: Cells) =>
  nomosFetch('PATCH', `/objects/${type}/records/${encodeURIComponent(id)}`, { values });
const archive = (type: string, id: string) => nomosFetch('DELETE', `/objects/${type}/records/${encodeURIComponent(id)}`);

/** Run `fn` over `items`, `width` at a time (bounded parallel record writes). */
async function eachLimited<T>(items: T[], width: number, fn: (x: T) => Promise<unknown>): Promise<void> {
  let i = 0;
  await Promise.all(Array.from({ length: Math.min(width, items.length) }, async () => { while (i < items.length) await fn(items[i++]); }));
}

/** A Sentinel id (or, for a record made on NomOS, its record id) -> the NomOS record id; 404 otherwise. */
async function recordIdOf(kind: IdKind, type: string, id: string, notFound: () => Error): Promise<string> {
  const rid = await resolveRecordId(kind, id);
  if (rid) return rid;
  if (UUID_RE.test(id) && (await cellsOf(type, [id], ['name'])).size) return id;
  throw notFound();
}
const siteId = (id: string, url = `/buildings/${id}`) =>
  recordIdOf('building', 'site', id, () => err(404, `Building ${id} not found. Verify the UUID is correct.`, url, 'building_not_found'));
const meterRid = (id: string, url = `/meter-points/${id}`) => recordIdOf('meter', 'meter', id, () => err(404, 'Meter point not found', url));

/** Sentinel-id <-> record-id for a whole vocabulary (energy sources, SEU categories): small, cached per session. */
const vocab = new Map<string, Promise<{ toRid: Map<string, string>; toSid: Map<string, string>; code: Map<string, string>; ridByCode: Map<string, string> }>>();
function vocabulary(type: string, sidSlug: string) {
  let p = vocab.get(type);
  if (!p) {
    p = allRows(type, ['code', sidSlug]).then((rows) => {
      const toRid = new Map<string, string>(); const toSid = new Map<string, string>(); const code = new Map<string, string>(); const ridByCode = new Map<string, string>();
      for (const r of rows) {
        const sid = str(r.v[sidSlug]) ?? r.id; toRid.set(sid, r.id); toRid.set(r.id, r.id); toSid.set(r.id, sid);
        const c = str(r.v.code); if (c) { code.set(r.id, c); ridByCode.set(c, r.id); }
      }
      return { toRid, toSid, code, ridByCode };
    });
    p.catch(() => vocab.delete(type));
    vocab.set(type, p);
  }
  return p;
}
export function clearBuildingsAdminCache(): void { vocab.clear(); tenantName = undefined; }
const energySources = () => vocabulary('energy_source', 'sentinel_energy_source_id');
const seuCategories = () => vocabulary('seu_category', 'sentinel_seu_category_id');

/** Who a confirmation is recorded as ("member:<id>", the asset confirmed_by convention "<kind>:<id>"). */
async function actor(): Promise<string> {
  const me = await getMe() as { member_id: string };
  return `member:${me.member_id}`;
}

// ---- buildings ---------------------------------------------------------------------------------------------------

const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']; // Google periods: 0 = Sunday
const hhmm = (t: string) => t.replace(':', '').slice(0, 4);

/** Sentinel OpeningHours {monday: {open: "08:00", close: "20:00"} | null, ...} -> NomOS Google periods (the stored shape). */
export function toPeriods(oh: Record<string, { open?: string | null; close?: string | null } | null | undefined> | null | undefined) {
  if (!oh) return null;
  const periods = DAYS.flatMap((d, i) => {
    const h = oh[d];
    if (!h || !h.open || !h.close) return [];
    return [{ open: { day: i, time: hhmm(h.open) }, close: { day: i, time: hhmm(h.close) } }];
  });
  return { periods };
}

const ADDRESS: Record<string, string> = { street_address: 'street', city: 'city', postal_code: 'postal_code', country_code: 'country',
  latitude: 'latitude', longitude: 'longitude' };
const DIRECT = ['name', 'size_sqm', 'building_type', 'timezone', 'external_system_ids', 'google_place_id', 'state_province',
  'operational_start_date', 'operational_end_date', 'sales_area_sqm', 'stockroom_area_sqm', 'social_area_sqm', 'other_area_sqm',
  'lease', 'contacts', 'building_attributes', 'is_active'];
// Sentinel BuildingUpdate: these may be set to null explicitly; any other null is "not given".
const NULLABLE = new Set(['operational_start_date', 'operational_end_date', 'climate_zone', 'opening_hours', 'sales_area_sqm',
  'stockroom_area_sqm', 'social_area_sqm', 'other_area_sqm', 'lease', 'contacts', 'building_attributes', 'latitude', 'longitude']);

/** The site cells a BuildingCreate/BuildingUpdate body writes. `location` merges onto the stored one. */
async function siteValues(data: Record<string, unknown>, storedLocation: Cells | null, url: string): Promise<Cells> {
  if (data.shelly_auth_key || data.shelly_server_url) {
    throw new NomosGapError('Shelly Cloud credentials are a Rhizome connection on NomOS (shelly.cloud), never a site cell: add the Shelly account under Connections');
  }
  const given = (k: string) => k in data && data[k] !== undefined && (data[k] !== null || NULLABLE.has(k));
  const out: Cells = {};
  for (const k of DIRECT) if (given(k)) out[k] = k === 'building_type' && typeof data[k] === 'string' ? (data[k] as string).toLowerCase() : data[k];
  // (0, 0) means "no coordinates" (Sentinel _normalize_zero_coordinates)
  if (data.latitude === 0 && data.longitude === 0) { data = { ...data, latitude: null, longitude: null }; }
  const addr = Object.keys(ADDRESS).filter(given);
  if (addr.length) {
    // hex_tile is derived by NomOS from latitude/longitude (migration 081: h3_from_latlon); writing it back is a 422
    const { hex_tile: _derived, ...stored } = storedLocation ?? {};
    const loc: Cells = { ...stored };
    for (const k of addr) {
      const v = k === 'country_code' && typeof data[k] === 'string' ? (data[k] as string).toUpperCase() : data[k];
      if (v === null) delete loc[ADDRESS[k]]; else loc[ADDRESS[k]] = v;
    }
    out.location = loc;
  }
  if (given('country_code')) {
    const cc = String(data.country_code).toUpperCase();
    const cid = await countryRecordId(cc);
    if (!cid) throw err(422, `Unknown country_code '${cc}'`, url, 'invalid_country');
    out.country_ref = cid;
  }
  if (given('opening_hours')) out.opening_hours = toPeriods(data.opening_hours as never);
  if (given('climate_zone')) {
    if (data.climate_zone === null) out.climate_zone = null;
    else {
      const zones = await allRows('climate_zone', ['code']);
      const z = zones.find((r) => str(r.v.code) === data.climate_zone);
      if (!z) throw err(422, `Unknown climate zone '${String(data.climate_zone)}'`, url);
      out.climate_zone = z.id;
    }
  }
  if (given('bidding_zone')) {
    if (data.bidding_zone === null) { out.bidding_zone = null; out.bidding_zone_source = null; out.bidding_zone_method = null; }
    else {
      const code = String(data.bidding_zone).toUpperCase();
      const zones = await allRows('bidding_zone', ['code'], { attribute: 'code', op: 'eq', value: code });
      if (!zones[0]) throw err(400, `Unknown bidding zone '${code}'`, url, 'unknown_bidding_zone');
      out.bidding_zone = zones[0].id; out.bidding_zone_source = 'manual'; out.bidding_zone_method = null;
    }
  }
  const mgrName = given('store_manager_name') ? (data.store_manager_name as string | null) : undefined;
  const mgrEmail = given('store_manager_email') ? (data.store_manager_email as string | null) : undefined;
  if (mgrName !== undefined || mgrEmail !== undefined) out.store_manager = await storeManager(mgrName ?? null, mgrEmail ?? null);
  return out;
}

/** The store manager is a Person record (SEN-24): found by email (else by name), created when new. Both empty: none. */
async function storeManager(name: string | null, email: string | null): Promise<string | null> {
  const e = email?.trim().toLowerCase() || null; const n = name?.trim() || null;
  if (!e && !n) return null;
  const hit = (await allRows('person', ['name', 'email'], e ? { attribute: 'email', op: 'eq', value: e } : { attribute: 'name', op: 'eq', value: n }))[0];
  if (hit) return hit.id;
  return createRecord('person', { name: n ?? e, ...(e ? { email: e } : {}), role: 'store_manager' });
}

async function createBuilding(data: BuildingCreate): Promise<Building> {
  const body = { timezone: 'Europe/Copenhagen', ...data } as Record<string, unknown>; // Sentinel BuildingBase default zone
  const values = await siteValues(body, null, '/buildings');
  values.is_active = true; // Sentinel buildings.is_active defaults to TRUE; NomOS lists filter on the cell
  const rid = await createRecord('site', values);
  return buildingByRecordId(rid);
}

async function updateBuilding(id: string, data: BuildingUpdate): Promise<Building> {
  const url = `/buildings/${id}`;
  const rid = await siteId(id);
  const touchesAddress = Object.keys(ADDRESS).some((k) => k in (data as object));
  const stored = touchesAddress ? (await cellsOf('site', [rid], ['location'])).get(rid)?.location : null;
  const loc = (Array.isArray(stored) ? stored[0] : stored) as Cells | null | undefined;
  const values = await siteValues(data as Record<string, unknown>, loc ?? null, url);
  if (!Object.keys(values).length) throw err(400, 'No fields to update', url, 'no_fields_to_update');
  await patch('site', rid, values);
  return buildingByRecordId(rid);
}

/** DELETE /buildings/{id}: the site, its meters, and (default true) its metering/actuating assets and initiatives are
 *  archived. NomOS archives instead of erasing: readings stay in Aion under the archived meters (reversible). */
async function deleteBuilding(id: string, options?: { delete_device_mappings?: boolean; delete_energy_data?: boolean; delete_initiatives?: boolean }): Promise<void> {
  const rid = await siteId(id);
  const atSite: NomosFilter = { attribute: 'site', op: 'eq', value: rid };
  const meters = await allRows('meter', [], atSite);
  const devices = options?.delete_device_mappings === false ? [] : await allRows('asset', [],
    { and: [atSite, { attribute: 'role', op: 'in', value: ['metering', 'actuating'] }] });
  const initiatives = options?.delete_initiatives === false ? [] : await allRows('initiative', [], atSite);
  await eachLimited(meters, 6, (r) => archive('meter', r.id));
  await eachLimited(devices, 6, (r) => archive('asset', r.id));
  await eachLimited(initiatives, 6, (r) => archive('initiative', r.id));
  await archive('site', rid);
}

// ---- legal companies & country responsibles ------------------------------------------------------------------------

const SITE_LE_FIELDS = ['country_ref', 'is_active', 'external_system_ids', 'legal_entity', 'legal_entity_confirmed_at', 'sentinel_building_id'];

async function siteFacts(): Promise<{ facts: R.SiteFacts[]; sid: Map<string, string> }> {
  const [rows, countries] = await Promise.all([allRows('site', SITE_LE_FIELDS), countryCodes()]);
  const sid = new Map<string, string>();
  const facts = rows.map((r) => {
    sid.set(r.id, str(r.v.sentinel_building_id) ?? r.id);
    const ext = (Array.isArray(r.v.external_system_ids) ? r.v.external_system_ids[0] : r.v.external_system_ids) as Cells | null;
    const code = ext && typeof ext === 'object' && typeof ext.jysk_code === 'string' ? ext.jysk_code : null;
    const c = ref(r.v.country_ref);
    return { id: r.id, country: c ? countries.get(c) ?? null : null, isActive: bool(r.v.is_active) !== false, code,
      legalEntity: ref(r.v.legal_entity), confirmed: !!str(r.v.legal_entity_confirmed_at) };
  });
  return { facts, sid };
}

async function entityFacts(): Promise<R.EntityFacts[]> {
  const [rows, countries] = await Promise.all([allRows('legal_entity', ['name', 'country_ref']), countryCodes()]);
  return rows.map((r) => ({ id: r.id, name: str(r.v.name) ?? '', country: countries.get(ref(r.v.country_ref) ?? '') ?? '' }))
    .sort((a, b) => a.id.localeCompare(b.id));
}

async function entityOf(id: string, url: string): Promise<R.EntityFacts> {
  const bySid = (await allRows('legal_entity', [], { attribute: 'sentinel_legal_entity_id', op: 'eq', value: id }))[0];
  const rid = bySid?.id ?? id;
  const e = (await entityFacts()).find((x) => x.id === rid);
  if (!e) throw err(404, 'Legal entity not found', url, 'legal_entity_not_found');
  return e;
}

let tenantName: Promise<string | null> | undefined;
async function customerName(): Promise<string | null> {
  tenantName ??= (async () => {
    const { tenant } = getConfig();
    const t = await nomosFetch<{ name?: string }>('GET', `/tenants/${encodeURIComponent(tenant)}`, undefined, { tenantScoped: false });
    return t.name ?? null;
  })();
  tenantName.catch(() => { tenantName = undefined; });
  return tenantName;
}

const compliance = async <T>(fn: () => Promise<T>, url: string): Promise<T> => {
  try { return await fn(); } catch (e) {
    if (e instanceof R.ComplianceError) throw err(e.status, e.message, url, e.code);
    throw e;
  }
};

async function proposeLegalEntities(dryRun: boolean): Promise<ProposeResult> {
  const [{ facts }, entities, name, countries] = await Promise.all([siteFacts(), entityFacts(), customerName(), countryCodes()]);
  const plans = R.planPropose(facts, entities, name);
  const countryRid = new Map([...countries].map(([rid, code]) => [code, rid]));
  const out = [];
  for (const p of plans) {
    let id = p.entity?.id ?? null;
    if (!dryRun && p.create) id = await createRecord('legal_entity', { name: p.name, country_ref: countryRid.get(p.country_code) });
    if (!dryRun && id) {
      const target = id;
      await eachLimited(p.assign, 6, (s) => patch('site', s, { legal_entity: target, legal_entity_confirmed_at: null, legal_entity_confirmed_by: null }));
    }
    out.push({ country_code: p.country_code, legal_entity_id: p.skipped_reason ? null : id, name: p.skipped_reason ? null : p.name,
      created: p.create, assigned: p.assign.length, skipped_reason: p.skipped_reason });
  }
  return { dry_run: dryRun, countries: out } as ProposeResult;
}

async function confirmLegalEntityBuildings(legalEntityId: string, dryRun: boolean): Promise<ConfirmResult> {
  const url = `/compliance/legal-entities/${legalEntityId}/confirm`;
  const entity = await entityOf(legalEntityId, url);
  const { facts, sid } = await siteFacts();
  const ids = R.planConfirm(entity.id, facts);
  if (!dryRun && ids.length) {
    const [at, by] = [new Date().toISOString(), await actor()];
    await eachLimited(ids, 6, (s) => patch('site', s, { legal_entity_confirmed_at: at, legal_entity_confirmed_by: by }));
  }
  return { legal_entity_id: legalEntityId, dry_run: dryRun, confirmed: sorted(ids.map((s) => sid.get(s) ?? s)) } as ConfirmResult;
}

async function assignBuildingsToLegalEntity(legalEntityId: string, body: AssignBody, dryRun: boolean): Promise<AssignResult> {
  const url = `/compliance/legal-entities/${legalEntityId}/buildings`;
  const b = body as AssignBody & { building_ids?: string[] };
  const entity = await entityOf(legalEntityId, url);
  const { facts, sid } = await siteFacts();
  // explicit building ids are Sentinel ids (or record ids of NomOS-made sites)
  const ridOf = new Map<string, string>(); for (const [rid, s] of sid) { ridOf.set(s, rid); ridOf.set(rid, rid); }
  const unknown = (b.building_ids ?? []).filter((i) => !ridOf.has(i));
  if (unknown.length) throw err(404, `Building id(s) not found for this customer: ${sorted(unknown).join(', ')}`, url, 'building_not_found');
  const plan = await compliance(async () => R.planAssign(entity, facts, {
    buildingIds: (b.building_ids ?? []).map((i) => ridOf.get(i) as string), storeCodes: b.store_codes,
    confirmed: b.confirmed, moveConfirmed: b.move_confirmed }), url);
  const confirmed = b.confirmed ?? true;
  const write = [...plan.assigned, ...plan.moved.map(([s]) => s)];
  if (!dryRun && write.length) {
    const cells = confirmed
      ? { legal_entity: entity.id, legal_entity_confirmed_at: new Date().toISOString(), legal_entity_confirmed_by: await actor() }
      : { legal_entity: entity.id, legal_entity_confirmed_at: null, legal_entity_confirmed_by: null };
    await eachLimited(write, 6, (s) => patch('site', s, cells));
  }
  const ext = (xs: string[]) => xs.map((s) => sid.get(s) ?? s);
  const list = (xs: string[]) => R.capped(ext(xs));
  return {
    legal_entity_id: legalEntityId, dry_run: dryRun,
    assigned: list(plan.assigned), assigned_count: plan.assigned.length,
    unchanged: list(plan.unchanged), unchanged_count: plan.unchanged.length,
    kept_confirmed: list(plan.kept_confirmed), kept_confirmed_count: plan.kept_confirmed.length,
    moved: R.capped(plan.moved).map(([s, from, to]) => ({ building_id: sid.get(s) ?? s, from_legal_entity_id: from, to_legal_entity_id: to })),
    moved_count: plan.moved.length,
    wrong_country: list(plan.wrong_country), wrong_country_count: plan.wrong_country.length,
    unmatched_codes: R.capped(plan.unmatched_codes), unmatched_codes_count: plan.unmatched_codes.length,
    ambiguous_codes: R.capped(plan.ambiguous_codes), ambiguous_codes_count: plan.ambiguous_codes.length,
  } as AssignResult;
}

interface Member { member_id: string; email: string; name?: string | null; display_name?: string | null; is_active?: boolean;
  first_name?: string | null; last_name?: string | null;
  role_code?: string | null; sentinel_user_id?: string | null }

async function members(): Promise<Member[]> {
  const { tenant } = getConfig();
  return nomosFetch<Member[]>('GET', `/tenants/${encodeURIComponent(tenant)}/members`, undefined, { tenantScoped: false });
}
/** A Sentinel user is an active member with a user role (agents and the service identity carry none; 'coo' is excluded as in Sentinel). */
const isUser = (m: Member) => m.is_active !== false && !!m.role_code && m.role_code !== 'coo';
const userId = (m: Member) => m.sentinel_user_id ?? m.member_id;
const contactSid = (r: Row) => str(r.v.sentinel_contact_id) ?? r.id;

async function getResponsibleCandidates(): Promise<ResponsibleCandidate[]> {
  const [contacts, ms] = await Promise.all([allRows('contact', ['name', 'email', 'sentinel_contact_id']), members()]);
  const live = contacts.filter((c) => str(c.v.email)).map((c) => ({ id: contactSid(c), name: str(c.v.name) ?? '', email: str(c.v.email) as string }));
  // a member's name as Sentinel builds it: first + last (display_name falls back to the email; Sentinel does that after sorting)
  const users = ms.filter(isUser).map((m) => ({ id: userId(m), name: [m.first_name, m.last_name].filter(Boolean).join(' ').trim(),
    email: m.email, linked: false }));
  return R.candidates(live, users) as ResponsibleCandidate[];
}

async function setCountryResponsible(countryCode: string, body: ResponsibleBody, dryRun: boolean): Promise<CountryResponsibleResult> {
  const url = `/compliance/countries/${encodeURIComponent(countryCode)}/responsible`;
  const [choice, cc] = await compliance(async () => [R.responsibleChoice(body as never), R.normalizeCountry(countryCode)] as const, url);
  const cid = await countryRecordId(cc);
  const active = cid ? await queryRecords('site', { filter: { and: [{ attribute: 'country_ref', op: 'eq', value: cid },
    { attribute: 'is_active', op: 'eq', value: true }] }, limit: 1 }) : null;
  if (!cid || !active?.total) throw err(422, 'The customer has no active buildings in that country', url, 'unknown_country');
  const [contacts, ms] = await Promise.all([allRows('contact', ['name', 'email', 'sentinel_contact_id']), members()]);
  const byEmail = (e: string) => contacts.find((c) => (str(c.v.email) ?? '').toLowerCase() === e.toLowerCase());
  let contact: { rid: string | null; sid: string | null; name: string; email: string };
  const fromRow = (c: Row) => ({ rid: c.id, sid: contactSid(c), name: str(c.v.name) ?? '', email: str(c.v.email) ?? '' });
  const fromUser = (m: Member) => {
    const hit = byEmail(m.email);
    return hit ? fromRow(hit) : { rid: null, sid: null, name: (m.display_name ?? m.name ?? m.email).trim(), email: m.email.toLowerCase() };
  };
  if (choice.kind === 'contact') {
    const hit = contacts.find((c) => contactSid(c) === choice.id || c.id === choice.id);
    if (!hit) throw err(404, 'Contact not found', url, 'contact_not_found');
    contact = fromRow(hit);
  } else if (choice.kind === 'user') {
    const m = ms.find((x) => isUser(x) && (userId(x) === choice.id || x.member_id === choice.id));
    if (!m) throw err(422, 'No active user or contact of this customer has that id or email; create the contact first', url, 'unknown_person');
    contact = fromUser(m);
  } else {
    const hit = byEmail(choice.email);
    const m = hit ? null : ms.find((x) => isUser(x) && x.email.toLowerCase() === choice.email.toLowerCase());
    if (!hit && !m) throw err(422, 'No active user or contact of this customer has that id or email; create the contact first', url, 'unknown_person');
    contact = hit ? fromRow(hit) : fromUser(m as Member);
  }
  if (!dryRun) {
    if (!contact.rid) { contact.rid = await createRecord('contact', { name: contact.name, email: contact.email }); contact.sid = contact.rid; }
    const current = ref((await cellsOf('country', [cid], ['responsible'])).get(cid)?.responsible);
    if (current !== contact.rid) await patch('country', cid, { responsible: contact.rid });
  }
  const user = ms.find((m) => isUser(m) && m.email.toLowerCase() === contact.email.toLowerCase());
  return { country_code: cc, contact_id: contact.sid as string, name: contact.name, email: contact.email,
    user_id: user ? userId(user) : null, dry_run: dryRun } as CountryResponsibleResult;
}

// ---- meter points ------------------------------------------------------------------------------------------------

const METER_FIELDS = ['name', 'site', 'energy_source', 'seu_category', 'physical_asset', 'parent_meter',
  'is_active', 'display_order', 'active_from', 'active_to', 'forecast_gaps', 'sentinel_meter_id', 'reading_entry'];

/** Rhizome provider -> Sentinel adapter id (the adapter vocabulary the Meters page renders). */
const ADAPTER: Record<string, string> = {
  'eloverblik.customer': 'dk-eloverblik', 'eloverblik.thirdparty': 'dk-eloverblik', 'shelly.cloud': 'shelly-cloud',
  datadis: 'es-datadis', eforsyning: 'dk-eforsyning', elkraft: 'se-elkraft', ista: 'dk-ista', kmd: 'dk-kmd-minforsyning', noova: 'no-noova',
};

export interface WireBinding { id: string; meter_record_id: string; connection_id: string; provider: string; connection_name: string;
  identity: { scheme: string; value: string }; quantity: string | null; watermark: string | null; loaded_from: string | null;
  last_error: { code?: string } | null }

async function bindingsOf(meterIds: string[]): Promise<WireBinding[]> {
  if (!meterIds.length) return [];
  return (await nomosFetch<NomosEnvelope<WireBinding[]>>('POST', '/meter-bindings/query', { meter_record_ids: meterIds })).data ?? [];
}

/** One meter's latest ACTUAL reading (NomOS POST /readings/latest-actual, owner ruling 2026-10-20). */
export interface WireLatestActual { meter: string; latest_actual_at: string | null; days_since: number | null }

/** the route's per-call bound (monad.readings.freshness.MAX_METERS) */
const LATEST_ACTUAL_CHUNK = 5000;

/** Per meter record id, its latest actual reading. A meter the read did not answer (it failed, or the meter is
 *  outside the caller's row scope) is absent: unknown, never "never read". */
async function latestActualOf(meterIds: string[]): Promise<Map<string, WireLatestActual>> {
  const out = new Map<string, WireLatestActual>();
  const chunks: string[][] = [];
  for (let i = 0; i < meterIds.length; i += LATEST_ACTUAL_CHUNK) chunks.push(meterIds.slice(i, i + LATEST_ACTUAL_CHUNK));
  try {
    const answers = await Promise.all(chunks.map((ids) =>
      nomosFetch<NomosEnvelope<{ rows: WireLatestActual[] }>>('POST', '/readings/latest-actual', { meter_ids: ids })));
    for (const a of answers) for (const r of a.data?.rows ?? []) out.set(r.meter, r);
  } catch {
    // the tile shows the average as unknown; every other number on the page stands
  }
  return out;
}

/** A meter's data sources: its Rhizome bindings (automatic) and, for a meter whose readings are typed by hand
 *  (meter.reading_entry = manual), the manual source. The manual source is not an object on NomOS: its id is
 *  `manual:<meter record id>`. */
export function dataSources(meterRid: string, v: Cells, bindings: WireBinding[]): DataSourceSummary[] {
  const auto = bindings.map((b) => ({
    id: b.id, adapter_id: ADAPTER[b.provider] ?? b.provider, source_system: b.connection_name, role: 'primary', is_active: true,
    is_manual: false, last_reading_at: b.watermark ?? undefined, latest_reading_at: b.watermark,
    deactivated_reason: null, is_reofferable: false, connection_id: b.connection_id,
  }));
  const manual = str(v.reading_entry) === 'manual'
    ? [{ id: `manual:${meterRid}`, adapter_id: 'manual', source_system: 'Manual Entry', role: 'primary', is_active: true, is_manual: true,
      latest_reading_at: null, deactivated_reason: null, is_reofferable: false, connection_id: null }]
    : [];
  return [...auto, ...manual] as unknown as DataSourceSummary[];
}

interface MeterContext {
  es: Awaited<ReturnType<typeof energySources>>; seu: Awaited<ReturnType<typeof seuCategories>>;
  meterSid: Map<string, string>; assets: Map<string, { sid: string; name: string }>; siteSid: Map<string, string>;
  siteName: Map<string, string>; bindings: Map<string, WireBinding[]>; actual: Map<string, WireLatestActual>;
}

async function meterContext(rows: Row[], parentSids: Map<string, string>): Promise<MeterContext> {
  const assetIds = [...new Set(rows.map((r) => ref(r.v.physical_asset)).filter((x): x is string => !!x))];
  const siteIds = [...new Set(rows.map((r) => ref(r.v.site)).filter((x): x is string => !!x))];
  const [es, seu, assets, sites, binds, actual] = await Promise.all([
    energySources(), seuCategories(), cellsOf('asset', assetIds, ['name', 'sentinel_asset_id']), cellsOf('site', siteIds, ['name', 'sentinel_building_id']),
    bindingsOf(rows.map((r) => r.id)), latestActualOf(rows.map((r) => r.id)),
  ]);
  const bindings = new Map<string, WireBinding[]>();
  for (const b of binds) bindings.set(b.meter_record_id, [...(bindings.get(b.meter_record_id) ?? []), b]);
  return {
    es, seu, meterSid: parentSids, bindings, actual,
    assets: new Map([...assets].map(([rid, c]) => [rid, { sid: str(c.sentinel_asset_id) ?? rid, name: str(c.name) ?? '' }])),
    siteSid: new Map([...sites].map(([rid, c]) => [rid, str(c.sentinel_building_id) ?? rid])),
    siteName: new Map([...sites].map(([rid, c]) => [rid, str(c.name) ?? ''])),
  };
}

/** Sentinel ids of the parent meters referenced by `rows` (and of the rows themselves). */
async function meterSids(rows: Row[]): Promise<Map<string, string>> {
  const out = new Map<string, string>(rows.map((r) => [r.id, str(r.v.sentinel_meter_id) ?? r.id]));
  const parents = [...new Set(rows.map((r) => ref(r.v.parent_meter)).filter((x): x is string => !!x && !out.has(x)))];
  for (const [rid, c] of await cellsOf('meter', parents, ['sentinel_meter_id'])) out.set(rid, str(c.sentinel_meter_id) ?? rid);
  return out;
}

/** One meter as Sentinel's MeterResponse (MeterWithBuildingResponse with `withBuilding`). created_at / updated_at are
 *  not served by the NomOS records list and no screen reads a meter's (grep of frontend/src): left out. The Data
 *  Latency status is classified from the newest binding watermark (data_sources[].latest_reading_at): see
 *  meterFreshness. The Data Freshness figure is the days since the latest ACTUAL reading: see actualFreshness. */
/** A meter's freshness on Rhizome's connection-freshness bounds (monad/rhizome/connection_freshness.py): a newest
 *  reading within 24 h is fresh, anything older is overdue (stale); no watermark is no_data. */
export function meterFreshness(sources: DataSourceSummary[], now = Date.now()): { status: MeterFreshnessStatus; latest_reading_at: string | null } {
  let latest: string | null = null;
  for (const s of sources) {
    const at = s.latest_reading_at;
    if (at && !Number.isNaN(Date.parse(at)) && (!latest || Date.parse(at) > Date.parse(latest))) latest = at;
  }
  if (!latest) return { status: 'no_data', latest_reading_at: null };
  return { status: now - Date.parse(latest) <= 24 * 3_600_000 ? 'fresh' : 'stale', latest_reading_at: latest };
}

/** The owner's freshness figure (2026-10-20) on a meter: days since its latest ACTUAL reading, from the server.
 *  A meter the read did not answer carries neither field (unknown); a never-read meter carries nulls. */
export function actualFreshness(a: WireLatestActual | undefined): { latest_actual_at?: string | null; days_since_actual?: number | null } {
  return a ? { latest_actual_at: a.latest_actual_at, days_since_actual: a.days_since } : {};
}

function toMeterPoint(r: Row, ctx: MeterContext, withBuilding: boolean): MeterPoint & { building_name?: string } {
  // Sentinel serves is_main_meter computed only on the bulk list; the per-building and single-meter responses read a
  // column migration 189 dropped, so they always answer false. 2026-10-20: no portfolio flag -- a main meter is a
  // top-level one (no parent meter); which days count is its active window.
  const v = r.v;
  const esRid = ref(v.energy_source); const seuRid = ref(v.seu_category); const assetRid = ref(v.physical_asset);
  const parent = ref(v.parent_meter); const site = ref(v.site) ?? '';
  const code = esRid ? ctx.es.code.get(esRid) ?? null : null;
  const asset = assetRid ? ctx.assets.get(assetRid) : undefined;
  const sources = dataSources(r.id, v, ctx.bindings.get(r.id) ?? []);
  return {
    id: ctx.meterSid.get(r.id) ?? r.id,
    building_id: ctx.siteSid.get(site) ?? site,
    ...(withBuilding ? { building_name: ctx.siteName.get(site) ?? '' } : {}),
    name: str(v.name) ?? '',
    meter_type: code as never,
    energy_source: code,
    energy_source_id: esRid ? ctx.es.toSid.get(esRid) ?? esRid : undefined,
    seu_category_id: seuRid ? ctx.seu.toSid.get(seuRid) ?? seuRid : null,
    forecast_gaps: bool(v.forecast_gaps) ?? false,
    is_main_meter: withBuilding && !parent,
    parent_meter_id: parent ? ctx.meterSid.get(parent) ?? parent : null,
    display_order: num(v.display_order) ?? 0,
    is_active: bool(v.is_active) ?? true,
    device_id: asset?.sid ?? null,
    channel_id: null,
    device_name: asset?.name ?? null,
    // Shelly vendor metadata (device type, online) is not on the NomOS asset: unknown, never guessed
    device_type: null,
    device_is_online: null,
    active_from: day(v.active_from),
    active_to: day(v.active_to),
    data_sources: sources,
    freshness: { ...meterFreshness(sources), ...actualFreshness(ctx.actual.get(r.id)) },
  } as unknown as MeterPoint & { building_name?: string };
}

/** Sentinel lists active meters by default. A FALSE is_active cell is an inactive meter; an absent one (a meter a
 *  connection's Map step made) is not recorded as inactive, so it is listed. */
const activeOnly = (rows: Row[], only: boolean) => (only ? rows.filter((r) => bool(r.v.is_active) !== false) : rows);

const byOrder = (a: MeterPoint, b: MeterPoint) => a.display_order - b.display_order || a.name.localeCompare(b.name);

async function getMeterPoints(buildingId: string, includeInactive = false): Promise<MeterPointListResponse> {
  const rid = await siteId(buildingId, `/buildings/${buildingId}/meter-points`);
  const rows = activeOnly(await allRows('meter', METER_FIELDS, { attribute: 'site', op: 'eq', value: rid }), !includeInactive);
  const ctx = await meterContext(rows, await meterSids(rows));
  const data = rows.map((r) => toMeterPoint(r, ctx, false)).sort(byOrder);
  return { data, total: data.length };
}

async function getAllMeterPoints(params?: { include_inactive?: boolean; building_id?: string }): Promise<BulkMeterPointListResponse> {
  const filter: NomosFilter | undefined = params?.building_id
    ? { attribute: 'site', op: 'eq', value: await siteId(params.building_id, '/meter-points') } : undefined;
  const rows = activeOnly(await allRows('meter', METER_FIELDS, filter), !params?.include_inactive);
  const ctx = await meterContext(rows, await meterSids(rows));
  const data = rows.map((r) => toMeterPoint(r, ctx, true))
    .sort((a, b) => (a.building_name ?? '').localeCompare(b.building_name ?? '') || byOrder(a, b));
  return { data, total: data.length } as BulkMeterPointListResponse;
}

async function meterById(rid: string): Promise<MeterPoint> {
  const v = (await cellsOf('meter', [rid], METER_FIELDS)).get(rid);
  if (!v) throw err(404, 'Meter point not found', `/meter-points/${rid}`);
  const rows = [{ id: rid, v }];
  return toMeterPoint(rows[0], await meterContext(rows, await meterSids(rows)), false);
}

/** _validate_parent_meter: exists (404), not itself (400), no cycle (400). Returns the parent's record id. */
async function parentOf(parentId: string, selfRid: string | null, url: string): Promise<string> {
  const prid = await recordIdOf('meter', 'meter', parentId, () => err(404, `Parent meter point ${parentId} not found`, url));
  if (selfRid && prid === selfRid) throw err(400, 'A meter point cannot be its own parent', url);
  if (selfRid) {
    const seen = new Set<string>();
    for (let cur: string | null = prid; cur && !seen.has(cur); cur = ref((await cellsOf('meter', [cur], ['parent_meter'])).get(cur)?.parent_meter)) {
      if (cur === selfRid) throw err(400, 'Setting this parent would create a cycle in the meter hierarchy', url);
      seen.add(cur);
    }
  }
  return prid;
}

/** A device (metering/actuating asset) of the meter's own site, else 400 as Sentinel. */
async function deviceOf(deviceId: string, siteRid: string, url: string): Promise<string> {
  const bad = () => err(400, "Device not found or doesn't belong to this building", url);
  const rid = await recordIdOf('asset', 'asset', deviceId, bad);
  const c = (await cellsOf('asset', [rid], ['site', 'role'])).get(rid);
  if (!c || ref(c.site) !== siteRid || !['metering', 'actuating'].includes(str(c.role) ?? '')) throw bad();
  return rid;
}

async function meterValues(data: MeterPointUpdate & { device_id?: string | null }, selfRid: string | null, siteRid: string, url: string): Promise<Cells> {
  const out: Cells = {};
  if (data.name !== undefined) out.name = data.name;
  if (data.energy_source_id !== undefined) {
    const es = await energySources();
    const rid = data.energy_source_id ? es.toRid.get(data.energy_source_id) : undefined;
    if (!rid) throw err(400, `Energy source ${data.energy_source_id} not found`, url);
    out.energy_source = rid;
  }
  if (data.seu_category_id !== undefined && data.seu_category_id !== null) {
    const rid = (await seuCategories()).toRid.get(data.seu_category_id);
    if (!rid) throw err(400, `SEU category ${data.seu_category_id} not found`, url);
    out.seu_category = rid;
  }
  if (data.parent_meter_id !== undefined) out.parent_meter = data.parent_meter_id === null ? null : await parentOf(data.parent_meter_id, selfRid, url);
  for (const k of ['display_order', 'is_active', 'active_from', 'active_to', 'forecast_gaps'] as const) if (data[k] !== undefined) out[k] = data[k];
  if (data.device_id !== undefined) out.physical_asset = data.device_id === null ? null : await deviceOf(data.device_id, siteRid, url);
  return out;
}

async function updateMeterPoint(meterPointId: string, data: MeterPointUpdate): Promise<MeterPoint> {
  const url = `/meter-points/${meterPointId}`;
  const rid = await meterRid(meterPointId, url);
  const site = ref((await cellsOf('meter', [rid], ['site'])).get(rid)?.site) ?? '';
  const values = await meterValues(data, rid, site, url);
  if (!Object.keys(values).length) throw err(400, 'No fields to update', url);
  await patch('meter', rid, values);
  return meterById(rid);
}

// ---- savings potential, assets ----------------------------------------------------------------------------------

/** POST /assets/confirm: provenance -> confirmed, with who and when; already-confirmed ones are counted and skipped. */
async function confirmAssets(ids: string[]): Promise<{ confirmed: number; already_confirmed: number; dry_run: boolean }> {
  const url = '/assets/confirm';
  const unique = [...new Set(ids)];
  const rids = await Promise.all(unique.map((i) => recordIdOf('asset', 'asset', i, () => err(404, `Asset ${i} not found`, url, 'not_found'))));
  const cells = await cellsOf('asset', rids, ['provenance']);
  const todo = rids.filter((r) => str(cells.get(r)?.provenance) !== 'confirmed');
  if (todo.length) {
    const [at, by] = [new Date().toISOString(), await actor()];
    await eachLimited(todo, 6, (r) => patch('asset', r, { provenance: 'confirmed', confirmed_at: at, confirmed_by: by }));
  }
  return { confirmed: todo.length, already_confirmed: rids.length - todo.length, dry_run: false };
}

async function importAssets(_fileId: string, _dryRun: boolean): Promise<never> {
  throw new NomosGapError('asset import: Sentinel parses an uploaded CSV/XLSX server-side (services/field_ops/asset_import.py, '
    + 'up to 5000 rows, classifier proposals) from a COO file id; NomOS has neither the file nor an import route. '
    + 'Smallest addition: an asset_import verb over a NomOS blob that writes asset records with the same rules');
}

export const buildingsAdminImpl: NomosRegistry = {
  createBuilding: createBuilding as unknown as AnyFn,
  updateBuilding: updateBuilding as unknown as AnyFn,
  deleteBuilding: deleteBuilding as unknown as AnyFn,
  proposeLegalEntities: proposeLegalEntities as unknown as AnyFn,
  confirmLegalEntityBuildings: confirmLegalEntityBuildings as unknown as AnyFn,
  assignBuildingsToLegalEntity: assignBuildingsToLegalEntity as unknown as AnyFn,
  getResponsibleCandidates: getResponsibleCandidates as unknown as AnyFn,
  setCountryResponsible: setCountryResponsible as unknown as AnyFn,
  getMeterPoints: getMeterPoints as unknown as AnyFn,
  getAllMeterPoints: getAllMeterPoints as unknown as AnyFn,
  updateMeterPoint: updateMeterPoint as unknown as AnyFn,
  confirmAssets: confirmAssets as unknown as AnyFn,
  importAssets: importAssets as unknown as AnyFn,
};
